import { useEffect, useRef, useState } from "react";
import { Link } from "wouter";
import { Heart, Share2, X } from "lucide-react";
import { api } from "../lib/api";
import { BlogLikeButton } from "./BlogLikeButton";

export type BlogCard = Awaited<ReturnType<typeof api.listBlog>>["posts"][number];

export function blogDate(value: string | null, long = false) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-PH", { year: "numeric", month: long ? "long" : "short", day: "numeric" });
}

export function blogInitials(name: string) {
  return name.trim().split(/\s+/).map(word => word[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
}

export function BlogStoryCard({ post, onShare, preview = false }: { post: BlogCard; onShare?: () => void; preview?: boolean }) {
  const [sharing, setSharing] = useState(false);
  const author = post.authorName || "KontrataPH Editorial";
  const href = `/blogs/${encodeURIComponent(post.slug)}`;
  const storyContent = <>
    <img src={post.coverImage || "/hero/professional-services.jpg"} alt="" loading="lazy" />
    <span className="bp-tag">{post.category || "Community"}</span>
    <h3>{post.title}</h3><p>{post.excerpt}</p>
  </>;
  return <article className="bp-card">
    <div className="bp-author"><span className="bp-avatar" aria-hidden="true">{blogInitials(author)}</span>
      <div><strong>{author}</strong><small>{preview ? "Draft preview" : `Published · ${blogDate(post.publishedAt)}`}</small></div>
    </div>
    {preview ? <div className="bp-open">{storyContent}</div> : <Link href={href} className="bp-open">{storyContent}</Link>}
    {/* [content.editorial-authoring](../../../docs/product/public-content.md#content-editorial-authoring): unsaved previews share the public layout without public navigation or reaction writes. */}
    <div className="bp-social-actions">
      {preview ? <button type="button" disabled className="bp-social-action disabled:!cursor-default"><Heart size={18} aria-hidden="true" /><span>Like</span></button> : <BlogLikeButton key={post.id} post={post} />}
      <button disabled={preview} className="bp-social-action disabled:!cursor-default" onClick={onShare || (() => setSharing(true))} aria-label={`Share ${post.title}`}><Share2 size={18} aria-hidden="true" /><span>Share</span></button>
    </div>
    {sharing && <BlogShareDialog post={post} onClose={() => setSharing(false)} />}
  </article>;
}

export function BlogShareDialog({ post, onClose }: { post: BlogCard; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [status, setStatus] = useState("");
  const url = `${window.location.origin}/blogs/${encodeURIComponent(post.slug)}`;

  useEffect(() => {
    const element = dialog.current;
    const previousFocus = document.activeElement;
    element?.showModal();
    return () => {
      element?.close();
      if (previousFocus instanceof HTMLElement) previousFocus.focus({ preventScroll: true });
    };
  }, []);

  async function copyLink() {
    try { await navigator.clipboard.writeText(url); setStatus("Link copied."); }
    catch { setStatus("Select and copy the story link below."); }
  }

  return <dialog ref={dialog} className="bp-share-dialog" aria-labelledby="blog-share-title" onCancel={event => { event.preventDefault(); onClose(); }} onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
    <div className="bp-share-inner">
      <button autoFocus className="bp-close" aria-label="Close share dialog" onClick={onClose}><X size={20} /></button>
      <Share2 size={28} aria-hidden="true" /><h2 id="blog-share-title">Share this story</h2><p>{post.title}</p>
      <label>Story link<input value={url} readOnly onFocus={event => event.target.select()} /></label>
      <button className="bp-more" onClick={copyLink}>Copy link</button>
      <p role="status">{status || "Copy the link to share this story."}</p>
    </div>
  </dialog>;
}
